/**
 * CapacityMapPanel: the map with its controls and the not-placed list.
 *
 * Layers: "Care managers" (home pins ringed in the CM's unit color, filled
 * with it when she is Open, with the Talk to Me gold or the Not Open red
 * for those stances, and grey on leave, read from the stance the server
 * sent; a row sent without one is filled with its unit color and its
 * label names no stance) and "Clients" (active clients in
 * one hue, distinct from every unit; hover shows the name and unit,
 * click opens the record). Toggle buttons for each layer, a unit filter
 * (a client's unit is its lead's), "show one care manager's clients"
 * (every client she is a care manager on, lead or additional, on the map
 * and in the not-placed list), and optional lines from that care manager
 * to the clients she leads (off by default).
 *
 * The Clients layer is fetched only for read_clinical_record holders and
 * the server scopes the pins to the records the caller may open. A pin
 * sits at the client's current location (the "currently at" address,
 * else the primary), and a pin placed only by town or ZIP says so in its
 * title. Clients with addresses who are not on the map are listed under
 * it, grouped by the reason (not found; not found with no state typed,
 * whose line names the tenant's working states and asks for the state;
 * no primary address; not checked yet), each name linking to the
 * record.
 *
 * The map opens on the tenant's working area: the box getTenantConfig
 * carries (computed by the server from staff home points), the whole
 * country when that read fails. The map mounts once the config has
 * settled, fits that first box once and keeps it for the panel's life,
 * so a refetched config never refits it. After that it moves only when
 * the person asks (LeafletMap's focus request): a client search choice
 * or a "Place a client" pin, and only to a point inside the working
 * area. A filter, a layer, a hover or new data never moves it. Every pin
 * is still drawn, however far outside the box.
 *
 * The client search (MapClientSearch, first in the toolbar, for
 * read_clinical_record holders) finds a client by name among the clients
 * the map would draw or list under its filters: the client pins under
 * the Unit and Care manager filters, whether or not the Clients layer is
 * on, and the not-placed rows under the Care manager filter. It reads
 * nothing. A placed choice turns the Clients layer on, draws that
 * client's pin found (grown, heavier, full opacity, never dimmed) and
 * moves the map to it when it lies inside the working area; outside it,
 * the map stays and a line under the toolbar says so. A choice not on
 * the map moves nothing and its chip names the reason. The found client
 * shows in a chip (MapClientChip) until its clear, a new choice, or a
 * filter or a new client read that leaves the client out; turning the
 * Clients layer off hides the pin, never the chip. A choice never touches
 * "Place a client", and "Place a client" never sets the found client.
 *
 * A legend (MapLegend) under the toolbar names every color the map
 * draws — the same unit colors and the same clients/prospects/placement-
 * pin colors (useMapColors, Admin > Units > Map colours) the pins
 * themselves use.
 *
 * Two more things ride the same map:
 *   - hovering (desktop) or tapping (touch) a care-manager pin shows a
 *     card beside it (CmCard): name, unit, the status the server sent,
 *     and one facts line (last week's hours and the target); it hides
 *     on mouseout or a second tap. The pin itself carries no Leaflet
 *     tooltip while the card can say the same thing (LeafletMap's
 *     fillLayer), so a care-manager is never described twice at once.
 *   - the "Place a client" toolbar button opens PlaceClientPanel in a
 *     column beside the map (the map stays clickable): type an address,
 *     pick a prospect, or click a client's marker (pickClientMode), then
 *     rank every care manager by distance to the pin and assign one. A
 *     new pin asks the map to move to it, which it does only inside the
 *     working area; otherwise the pin is drawn, the map stays, and the
 *     panel says so.
 *   - with `initialProspectId` (the Team map's ?prospect=), the panel
 *     starts with Place a client open in Prospect mode on that card; the
 *     id is spent when the panel closes, so reopening it from the
 *     toolbar starts fresh.
 *
 * Picking a care manager while placing — from the ranked list (down in
 * PlaceClientPanel) or by clicking their node on the map — is tracked
 * here as selectedCmId, because both the map (the ray, the pin's
 * persistent hover ring) and the panel (the row highlight) need it. One
 * ray draws pin-rim to CM-rim (LeafletMap's selectionLine, the same
 * trimmed-ray drawing the "lines to clients" layer uses) with the
 * ranked list's own distance as a midpoint chip; picking a different CM
 * moves it, picking the same one again clears it, and closing "Place a
 * client" clears it along with everything else. While a CM is selected,
 * the Clients layer is forced on (and the caller's prior on/off choice
 * restored the moment the selection clears) and every client pin dims
 * to LeafletMap's DIMMED_PIN_OPACITY except the clients that CM is on,
 * lead or additional. Nothing is ever removed from the map, just faded,
 * so the reader sees the new pin sitting inside the caseload it would
 * join.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { FilterPill } from '@/components/primitives/FilterPill';
import { unitDotColor } from '@/components/workspace/workspaceFormat';
import { STANCE_FILL, STATED_AVAILABILITY_LABEL, formatHours, type StatedAvailability } from '@/lib/capacity';
import {
  tileUrlTemplate,
  useMapColors,
  useMapProspects,
  type ClientMapWire,
  type CmCapacityRowWire,
} from '@/lib/capacityApi';
import { CM_ON_LEAVE_FILL, CM_RING_HOVER_WEIGHT, CM_RING_WEIGHT, DISTANCE_RAY_COLOR, UNASSIGNED_CM_COLOR } from '@/lib/mapColors';
import { haversineMiles } from '@/lib/geo';
import { APPROXIMATE_SUFFIX, clientNotPlacedLabel } from '@/lib/clientLocation';
import { capitalizeFirst } from '@/lib/capitalizeFirst';
import { insideBounds, readWorkingArea, type MapBounds } from '@/lib/mapView';
import { readWorkingStates } from '@/lib/tenantConfig';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import type { UnitCoverageRow } from '@/lib/api';
import { CmCard } from './CmCard';
import {
  CLIENT_PIN_OPACITY,
  CLIENT_PIN_RADIUS,
  CM_PIN_RADIUS,
  DEFAULT_MAP_HEIGHT_CLASS,
  HOVER_RADIUS_DELTA,
  HOVER_WEIGHT,
  LeafletMap,
  PLACEMENT_PIN_RADIUS,
  useFocusRequest,
  type MapLine,
  type MapPin,
} from './LeafletMap';
import { MapClientChip, type MapClientChipState } from './MapClientChip';
import { MapClientSearch } from './MapClientSearch';
import { MapLegend } from './MapLegend';
import { NotPlacedGroup } from './NotPlacedGroup';
import { PLACEMENT_OUTSIDE_AREA_TEXT, PlaceClientPanel, type PickedClientPin, type PlaceClientMode } from './PlaceClientPanel';
import { SelectField } from '@/components/primitives/SelectField';

export interface CapacityMapPanelProps {
  tenantSlug: string;
  careManagers: CmCapacityRowWire[];
  /** null while loading or when the caller may not read client records. */
  clientMap: ClientMapWire | null;
  canShowClients: boolean;
  clientsLoading?: boolean;
  clientsError?: boolean;
  /** Height classes for the map canvas (the Map tab makes it fill the page). */
  mapHeightClass?: string;
  /** GET /units/coverage rows, for "place a client"'s unit-by-county
   *  lookup. Empty when not yet loaded — the panel just names no unit. */
  unitCoverage?: UnitCoverageRow[];
  /** manage_work_items: whether the Assign button renders for a prospect
   *  (intake-card) row in "place a client". */
  canAssignProspect?: boolean;
  /** manage_client_identity: whether the Assign button renders for an
   *  existing-client row in "place a client". */
  canAssignClient?: boolean;
  /** POST /work-items/{id}/reassign, reused as-is. */
  onAssignProspect?: (workItemId: string, rosterId: string) => Promise<void>;
  /** POST /clients/{id}/assign-cm, reused as-is. */
  onAssignClient?: (clientId: string, rosterId: string) => Promise<void>;
  /** An intake card to open Place a client on, in Prospect mode, when the
   *  panel mounts (the Team map's ?prospect=). Null opens nothing. */
  initialProspectId?: string | null;
}

interface UnitOption {
  id: string;
  name: string;
}

interface HoveredCm {
  rosterId: string;
  point: { x: number; y: number };
}

/** The not-placed list's groups, in this order: an address to fix, then
 *  one typed with no state, then a primary to choose, then the addresses
 *  still waiting on their first check, then the clients with no address on
 *  file (LP-42). */
const NOT_PLACED_GROUP_ORDER: readonly ClientMapWire['not_placed'][number]['reason'][] = [
  'not_found',
  'not_found_add_state',
  'no_primary',
  'not_checked',
  'no_address',
];

/** A care-manager pin's centre: the unit color when she is Open (or the
 *  row carries no stance), the stance fill for Talk to Me and Not Open,
 *  and the grey on leave. */
function pinCentre(stance: StatedAvailability | undefined, unit: string): string {
  if (stance === undefined || stance === 'open') return unit;
  if (stance === 'on_leave') return CM_ON_LEAVE_FILL;
  return STANCE_FILL[stance];
}

export function CapacityMapPanel({
  tenantSlug,
  careManagers,
  clientMap,
  canShowClients,
  clientsLoading = false,
  clientsError = false,
  mapHeightClass,
  unitCoverage = [],
  canAssignProspect = false,
  canAssignClient = false,
  onAssignProspect,
  onAssignClient,
  initialProspectId = null,
}: CapacityMapPanelProps): ReactNode {
  const navigate = useNavigate();
  const [showCms, setShowCms] = useState(true);
  const [showClients, setShowClients] = useState(false);
  const [unitFilter, setUnitFilter] = useState<string>('');
  const [cmFilter, setCmFilter] = useState<string>('');
  const [showLines, setShowLines] = useState(false);

  const [hoveredCm, setHoveredCm] = useState<HoveredCm | null>(null);
  const [placing, setPlacing] = useState(initialProspectId !== null);
  const [placementMode, setPlacementMode] = useState<PlaceClientMode>(initialProspectId !== null ? 'prospect' : 'address');
  // The landing card, until the panel first closes.
  const [landingProspectId, setLandingProspectId] = useState<string | null>(initialProspectId);
  const [placementPin, setPlacementPin] = useState<{ lat: number; lng: number; color: string } | null>(null);
  const [pickedClientPin, setPickedClientPin] = useState<PickedClientPin | null>(null);
  // The person's asks to move the map: a placement pin, a search choice.
  const [focusRequest, askFocus] = useFocusRequest();
  // The client the search chose; shown only while among the candidates.
  const [foundClientId, setFoundClientId] = useState<string | null>(null);

  // "Place a client"'s picked care manager — set from the ranked list's
  // row click or from clicking their node on the map (both funnel
  // through selectCm below), read by the ray, the pin's persistent
  // hover ring, the client-pin dimming, and the ranked list's own row
  // highlight.
  const [selectedCmId, setSelectedCmId] = useState<string | null>(null);
  const selectCm = (rosterId: string): void => setSelectedCmId((prev) => (prev === rosterId ? null : rosterId));
  const clearPlacement = (): void => {
    setPlacing(false);
    setPlacementMode('address');
    setLandingProspectId(null);
    setPlacementPin(null);
    setPickedClientPin(null);
    setSelectedCmId(null);
  };

  // While a CM is selected, the Clients layer is forced on so their
  // caseload is visible around the new pin — restoring the caller's own
  // on/off choice the instant the selection clears (a second click on
  // the same CM, or closing "Place a client"). Keyed on selectedCmId's
  // own null/non-null transition, not its value: moving the ray from
  // one CM to another is not a fresh "selection started" event, so it
  // must not re-capture showClients (which by then just reflects the
  // FORCED-on state, not the caller's real prior choice).
  const priorShowClientsRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!canShowClients) return;
    if (selectedCmId !== null && priorShowClientsRef.current === null) {
      priorShowClientsRef.current = showClients;
      if (!showClients) setShowClients(true);
    } else if (selectedCmId === null && priorShowClientsRef.current !== null) {
      const prior = priorShowClientsRef.current;
      priorShowClientsRef.current = null;
      if (prior !== showClients) setShowClients(prior);
    }
    // showClients deliberately excluded: this effect reacts only to
    // selectedCmId's transition, reading whatever showClients current
    // IS at that moment — a showClients change on its own (the caller
    // toggling the pill mid-selection) is not a reason to re-fire it.
  }, [selectedCmId, canShowClients]);

  // The three tenant-configurable colors (Admin > Units > Map colours) —
  // resolved with brand-adjacent defaults, so the map never waits on this
  // read to draw something reasonable.
  const colors = useMapColors(tenantSlug);

  // The tenant's working states, named by the add-state line.
  const tenantConfig = useTenantConfig(tenantSlug);
  const workingStates = useMemo(() => readWorkingStates(tenantConfig.data), [tenantConfig.data]);

  // The working area the map opens on: read once the config has settled
  // (the whole country when it failed), then kept for the panel's life.
  const workingAreaRef = useRef<MapBounds | null>(null);
  if (workingAreaRef.current === null && !tenantConfig.isPending) {
    workingAreaRef.current = readWorkingArea(tenantConfig.data);
  }
  const workingArea = workingAreaRef.current;
  const pinOutsideWorkingArea = placementPin !== null && workingArea !== null && !insideBounds(placementPin, workingArea);

  const units = useMemo<UnitOption[]>(() => {
    const seen = new Map<string, string>();
    for (const cm of careManagers) if (cm.unit_id && cm.unit_name) seen.set(cm.unit_id, cm.unit_name);
    for (const pin of clientMap?.pins ?? []) if (pin.unit_id && pin.unit_name) seen.set(pin.unit_id, pin.unit_name);
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [careManagers, clientMap]);

  // The legend's per-unit swatches: every unit the board knows about, in
  // the SAME color a CM pin from that unit is filled with (unitDotColor —
  // the unit's own configured color, or the deterministic palette
  // fallback when it hasn't set one).
  const legendUnits = useMemo(
    () => units.map((u) => ({ id: u.id, name: u.name, color: unitDotColor(u.id, careManagers.find((cm) => cm.unit_id === u.id)?.unit_color ?? null)! })),
    [units, careManagers],
  );
  const hasUnassignedCm = careManagers.some((cm) => cm.unit_id === null);

  const visibleCms = useMemo(
    () =>
      careManagers.filter((cm) => (unitFilter === '' || cm.unit_id === unitFilter) && (cmFilter === '' || cm.roster_id === cmFilter)),
    [careManagers, unitFilter, cmFilter],
  );

  const CM_PIN_BASE_RADIUS = 9;

  const cmPins = useMemo<MapPin[]>(
    () =>
      visibleCms
        // A point on file only: a row sent without its home (a seat that
        // does not assign) draws no pin, as a row with none on file.
        .filter((cm) => cm.home !== null && cm.home !== undefined)
        .map((cm) => {
          // The stance is the one the server sent; a row sent without it
          // names none, in the label or the centre.
          const stance = cm.new_client_stance;
          const lastWeek = `${formatHours(cm.hours_worked_last_week) || '0'} h`;
          const factText = stance === undefined ? `Last week ${lastWeek}` : `${STATED_AVAILABILITY_LABEL[stance]}, last week ${lastWeek}`;
          // "Place a client"'s selected care manager gets the SAME grown
          // radius + heavier ring a live mouseover already produces
          // (HOVER_RADIUS_DELTA, CM_RING_HOVER_WEIGHT), persistent instead
          // of transient, so the node the ray points to reads as picked
          // even after the pointer moves away.
          const selected = cm.roster_id === selectedCmId;
          const unit = unitDotColor(cm.unit_id, cm.unit_color) ?? UNASSIGNED_CM_COLOR;
          return {
            id: cm.roster_id,
            lat: cm.home!.lat,
            lng: cm.home!.lng,
            // Every CM pin is a filled circle ringed in her unit's color
            // (unitDotColor: the unit's own configured color, or the
            // deterministic palette fallback; no unit at all is
            // UNASSIGNED_CM_COLOR). The centre is the unit color when
            // she is Open, and for a row sent without a stance; the stance
            // fill for Talk to Me and Not Open; CM_ON_LEAVE_FILL on leave.
            // The ring is CM_RING_WEIGHT wide so a pale unit still reads
            // as its hue.
            color: pinCentre(stance, unit),
            ringColor: unit,
            filled: true,
            // An approximate home (a town-level answer) says so, as client
            // and prospect pins do.
            title: `${cm.full_name}: ${factText}${cm.unit_name ? ` (${cm.unit_name})` : ''}${cm.home_precision === 'zip' ? APPROXIMATE_SUFFIX : ''}`,
            href: null,
            radius: selected ? CM_PIN_BASE_RADIUS + HOVER_RADIUS_DELTA : CM_PIN_BASE_RADIUS,
            weight: selected ? CM_RING_HOVER_WEIGHT : CM_RING_WEIGHT,
            hoverWeight: CM_RING_HOVER_WEIGHT,
          };
        }),
    [visibleCms, selectedCmId],
  );

  // The care manager pick keeps every client she is a care manager on, lead
  // or additional (cm_roster_ids). The unit filter reads the pin's unit,
  // which is the lead's.
  const visibleClientPins = useMemo(
    () =>
      (clientMap?.pins ?? []).filter(
        (p) => (unitFilter === '' || p.unit_id === unitFilter) && (cmFilter === '' || p.cm_roster_ids.includes(cmFilter)),
      ),
    [clientMap, unitFilter, cmFilter],
  );

  // The found client, derived from the search's candidates: a filter or a
  // new client read that leaves them out clears them.
  const foundPin = foundClientId === null ? null : (visibleClientPins.find((p) => p.client_id === foundClientId) ?? null);
  const foundNotPlaced =
    foundClientId === null || foundPin !== null
      ? null
      : ((clientMap?.not_placed ?? []).find((c) => c.client_id === foundClientId && (cmFilter === '' || c.cm_roster_ids.includes(cmFilter))) ?? null);
  const foundGone = foundClientId !== null && foundPin === null && foundNotPlaced === null;
  useEffect(() => {
    if (foundGone) setFoundClientId(null);
  }, [foundGone]);

  const clientPins = useMemo<MapPin[]>(
    () =>
      // Clients are a second CLASS of thing next to care managers, not a
      // sixth unit — one hue for every client pin (colors.clients, the
      // tenant's configured value or a brand-adjacent default), distinct
      // from every unit color, so the client layer reads at a glance
      // instead of competing with the unit filter's own color coding.
      // Which unit a client belongs to is the Unit filter's job, not the
      // pin's fill.
      // The search's found client is drawn found (grown, heavier, full
      // opacity, never dimmed) and last, so it sits on top.
      [...visibleClientPins]
        .sort((a, b) => Number(a.client_id === foundClientId) - Number(b.client_id === foundClientId))
        .map((p) => {
          const found = p.client_id === foundClientId;
          return {
            id: p.client_id,
            lat: p.lat,
            lng: p.lng,
            color: colors.clients,
            title: `${p.display_name}${p.unit_name ? ` (${p.unit_name})` : ''}${p.precision === 'zip' ? APPROXIMATE_SUFFIX : ''}`,
            href: `/t/${tenantSlug}/clients/${p.client_id}`,
            // "Place a client": while a care manager is selected, every client
            // she is not a care manager on (lead or additional) dims, so her
            // own caseload, still at full opacity, reads clearly around the
            // new pin. Nothing is hidden; a filtered-out client was already
            // absent from visibleClientPins before this ran.
            dimmed: !found && selectedCmId !== null && !p.cm_roster_ids.includes(selectedCmId),
            opacity: found ? 1 : CLIENT_PIN_OPACITY,
            // LP-43: the found client carries their name tag beside the
            // pin; a click opens the record, as every client pin does.
            ...(found ? { radius: CLIENT_PIN_RADIUS + HOVER_RADIUS_DELTA, weight: HOVER_WEIGHT, tag: capitalizeFirst(p.display_name) } : {}),
          };
        }),
    [visibleClientPins, tenantSlug, colors.clients, selectedCmId, foundClientId],
  );

  // Prospects (open intake cards with a client row): a third marker
  // class, drawn only when the Clients layer is fetchable and on (the
  // same clinical-read floor the Clients layer itself uses). Only the
  // placeable ones have a point to draw; the rest are still in the
  // "place a client" picker, where their reason shows.
  const { prospects } = useMapProspects(tenantSlug, { enabled: canShowClients });
  const prospectPins = useMemo<MapPin[]>(
    () =>
      prospects
        .filter((p) => p.placeable && p.lat !== null && p.lng !== null)
        .map((p) => ({
          id: p.work_item_id,
          lat: p.lat!,
          lng: p.lng!,
          color: colors.prospects,
          title: `${p.display_name} (prospect)${p.precision === 'zip' ? APPROXIMATE_SUFFIX : ''}`,
          href: null,
          filled: false,
        })),
    [prospects, colors.prospects],
  );

  const lines = useMemo<MapLine[]>(() => {
    if (!showLines || cmFilter === '') return [];
    const cm = careManagers.find((c) => c.roster_id === cmFilter);
    if (!cm || !cm.home) return [];
    const from: [number, number] = [cm.home.lat, cm.home.lng];
    // Flat sage, not per-unit: with client pins reduced to one hue, a
    // per-unit ray color would reintroduce the busyness the paper base is
    // meant to keep off the map. fromRadius/toRadius are the actual radii
    // LeafletMap draws the CM and client pins at, so the ray is trimmed
    // to those pins' real rims (LeafletMap's trimSegmentPx), not a
    // guessed constant. A ray joins the picked care manager to every client
    // she is on, as the lead or as an additional care manager.
    return visibleClientPins
      .filter((p) => p.cm_roster_ids.includes(cmFilter))
      .map((p) => ({
        from,
        to: [p.lat, p.lng],
        color: DISTANCE_RAY_COLOR,
        fromRadius: CM_PIN_RADIUS,
        toRadius: CLIENT_PIN_RADIUS,
      }));
  }, [showLines, cmFilter, careManagers, visibleClientPins]);

  // "Place a client"'s own ray: the placement pin to the selected care
  // manager, independent of the unit/CM filters and the "Lines to
  // clients" toggle above — a different workflow, reusing the same
  // trimmed-ray drawing (LeafletMap's selectionLine layer) with the
  // placement pin's own radius at one end and CM_PIN_RADIUS at the
  // other. The label is the exact distance text the ranked list shows
  // for this care manager (same haversineMiles call, same .toFixed(1)),
  // not a second source of truth for it.
  const selectionLine = useMemo<MapLine | null>(() => {
    if (!placementPin || !selectedCmId) return null;
    const cm = careManagers.find((c) => c.roster_id === selectedCmId);
    if (!cm || !cm.home) return null;
    const distanceMiles = haversineMiles({ lat: placementPin.lat, lng: placementPin.lng }, cm.home);
    return {
      from: [placementPin.lat, placementPin.lng],
      to: [cm.home.lat, cm.home.lng],
      color: DISTANCE_RAY_COLOR,
      fromRadius: PLACEMENT_PIN_RADIUS,
      toRadius: CM_PIN_RADIUS,
      label: `${distanceMiles.toFixed(1)} mi`,
    };
  }, [placementPin, selectedCmId, careManagers]);

  // The care manager pick narrows this list the same way it narrows the pins.
  const notPlaced = useMemo(
    () => (clientMap?.not_placed ?? []).filter((c) => cmFilter === '' || c.cm_roster_ids.includes(cmFilter)),
    [clientMap, cmFilter],
  );
  // Grouped by reason (NOT_PLACED_GROUP_ORDER); an entry carrying no
  // reason this page knows is still listed, ungrouped.
  const notPlacedGroups = NOT_PLACED_GROUP_ORDER.map((reason) => ({
    reason,
    items: notPlaced
      .filter((c) => c.reason === reason)
      .map((c) => ({ id: c.client_id, label: c.display_name, href: `/t/${tenantSlug}/clients/${c.client_id}` })),
  })).filter((g) => g.items.length > 0);
  const notPlacedUngrouped = notPlaced.filter((c) => !NOT_PLACED_GROUP_ORDER.includes(c.reason));
  const notPlacedWithAddress = notPlaced.filter((c) => c.reason !== 'no_address').length;
  // Named only when the server said none is on file (home: null); a row
  // sent without the key is withheld, not missing.
  const cmsWithoutHome = visibleCms.filter((cm) => cm.home === null);

  // A search choice: a placed client turns the Clients layer on (and, while
  // a care manager is selected in "Place a client", the choice it will
  // restore) and asks the map to move to them; one not on the map asks
  // nothing.
  const chooseFoundClient = (clientId: string): void => {
    setFoundClientId(clientId);
    const pin = visibleClientPins.find((p) => p.client_id === clientId);
    if (!pin) return;
    if (priorShowClientsRef.current !== null) priorShowClientsRef.current = true;
    setShowClients(true);
    askFocus({ lat: pin.lat, lng: pin.lng });
  };

  // A new "Place a client" pin asks the map to move to it.
  const onPlacementPinChange = useCallback(
    (pin: { lat: number; lng: number; color: string } | null): void => {
      setPlacementPin(pin);
      if (pin) askFocus(pin);
    },
    [askFocus],
  );

  const foundChip: MapClientChipState | null = foundPin
    ? { kind: 'placed', name: foundPin.display_name, approximate: foundPin.precision === 'zip' }
    : foundNotPlaced
      ? { kind: 'not_placed', name: foundNotPlaced.display_name, reasonLabel: clientNotPlacedLabel(foundNotPlaced.reason, workingStates) }
      : null;
  const foundOutsideWorkingArea = foundPin !== null && workingArea !== null && !insideBounds(foundPin, workingArea);

  return (
    <section data-testid="capacity-map" aria-label="Map">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {canShowClients && (
          <MapClientSearch
            pins={visibleClientPins}
            notPlaced={notPlaced}
            workingStates={workingStates}
            loading={clientsLoading}
            error={clientsError}
            filtersActive={unitFilter !== '' || cmFilter !== ''}
            onPick={chooseFoundClient}
          />
        )}
        {foundChip && (
          <MapClientChip
            state={foundChip}
            placedPrefix="Showing"
            href={`/t/${tenantSlug}/clients/${foundClientId}`}
            testId="map-found-client-chip"
            clearTestId="map-found-client-clear"
            onClear={() => setFoundClientId(null)}
          />
        )}
        <FilterPill
          label="Care managers"
          active={showCms}
          onClick={() => setShowCms((v) => !v)}
          data-testid="map-toggle-cms"
        />
        {canShowClients && (
          <FilterPill
            label="Clients"
            active={showClients}
            onClick={() => setShowClients((v) => !v)}
            data-testid="map-toggle-clients"
          />
        )}
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          <span>Unit</span>
          <SelectField size="compact"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
            data-testid="map-unit-filter"
          >
            <option value="">All units</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </SelectField>
        </label>
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          <span>Care manager</span>
          <SelectField size="compact"
            value={cmFilter}
            onChange={(e) => setCmFilter(e.target.value)}
            data-testid="map-cm-filter"
          >
            <option value="">Everyone</option>
            {careManagers.map((cm) => (
              <option key={cm.roster_id} value={cm.roster_id}>{cm.full_name}</option>
            ))}
          </SelectField>
        </label>
        {canShowClients && cmFilter !== '' && showClients && (
          <FilterPill
            label="Lines to clients"
            active={showLines}
            onClick={() => setShowLines((v) => !v)}
            data-testid="map-toggle-lines"
          />
        )}
        <Button
          variant={placing ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            if (placing) {
              clearPlacement();
            } else {
              setPlacing(true);
            }
          }}
          data-testid="map-place-client-toggle"
        >
          Place a client
        </Button>
      </div>
      {foundOutsideWorkingArea && (
        <p className="-mt-1.5 mb-2 text-xs text-ink-soft" data-testid="map-client-search-outside-area">
          {PLACEMENT_OUTSIDE_AREA_TEXT}
        </p>
      )}

      <MapLegend
        units={legendUnits}
        showUnassigned={hasUnassignedCm}
        unassignedColor={UNASSIGNED_CM_COLOR}
        clientColor={colors.clients}
        prospectColor={colors.prospects}
        placementPinColor={colors.placement_pin}
        showStanceKey={careManagers.some((cm) => cm.new_client_stance !== undefined)}
      />

      {/* "Place a client" sits BESIDE the map as its own column, not over
          it: the panel grows with its ranked list and the page scrolls,
          instead of the panel scrolling inside the map's height and
          covering the map's right-hand third. On a narrow screen it
          stacks under the map. The map wrapper stays `relative` for the
          hover card's absolute placement. */}
      <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-start" data-testid="map-with-placement">
        <div className="relative w-full min-w-0 flex-1">
          {workingArea === null ? (
            <div
              data-testid="capacity-map-loading"
              className={`${mapHeightClass ?? DEFAULT_MAP_HEIGHT_CLASS} flex w-full items-center justify-center rounded-card bg-sand text-sm text-ink-soft`}
            >
              Loading the map.
            </div>
          ) : (
          <LeafletMap
          tileUrl={tileUrlTemplate(tenantSlug)}
          homeBounds={workingArea}
          workingArea={workingArea}
          cmPins={cmPins}
          clientPins={clientPins}
          lines={lines}
          prospectPins={prospectPins}
          // Drawn in the tenant's placement-pin colour as it is now, so a
          // pin set before the map colours loaded (a card picked on arrival
          // from the intake card's Show on map) takes the tenant's colour
          // when they arrive, without asking the map to move again.
          placementPin={placementPin ? { ...placementPin, color: colors.placement_pin } : null}
          focusRequest={focusRequest}
          selectionLine={selectionLine}
          showCms={showCms}
          showClients={canShowClients && showClients}
          onOpen={(href) => navigate(href)}
          heightClass={mapHeightClass}
          onCmPinHover={(rosterId, point) => setHoveredCm(rosterId && point ? { rosterId, point } : null)}
          onCmPinTap={(rosterId, point) => {
            setHoveredCm((prev) => (prev?.rosterId === rosterId ? null : { rosterId, point }));
            // "Place a client": a click on a CM's node selects them for
            // the ray the exact same way a ranked-list row click does
            // (both funnel through selectCm) — alongside, not instead
            // of, the existing hover/tap card above.
            if (placing) selectCm(rosterId);
          }}
          pickClientMode={placing && placementMode === 'client'}
          onPickClientPin={(clientId, lat, lng) => {
            const found = clientMap?.pins.find((p) => p.client_id === clientId);
            setPickedClientPin({
              clientId,
              lat,
              lng,
              countyName: found?.county_name ?? null,
              precision: found?.precision ?? 'street',
              displayName: found?.display_name ?? 'Client',
              // SPICE Train A2: falls back to 'care_management' (the
              // schema default) in the unreachable case the pin wasn't
              // found in the already-loaded clientMap — the picker only
              // ever offers ids that ARE in clientMap.pins.
              engagement: found?.engagement ?? 'care_management',
            });
          }}
        />
          )}

        {hoveredCm &&
          (() => {
            const row = careManagers.find((c) => c.roster_id === hoveredCm.rosterId);
            if (!row) return null;
            return (
              <div
                className="absolute z-[70]"
                style={{ left: hoveredCm.point.x + 8, top: Math.max(0, hoveredCm.point.y - 8) }}
              >
                <CmCard row={row} />
              </div>
            );
          })()}
      </div>

        {placing && (
          <PlaceClientPanel
            tenantSlug={tenantSlug}
            careManagers={careManagers}
            unitCoverage={unitCoverage}
            canAssignProspect={canAssignProspect}
            canAssignClient={canAssignClient}
            pickedClient={pickedClientPin}
            placementPinColor={colors.placement_pin}
            pinOutsideWorkingArea={pinOutsideWorkingArea}
            initialProspectId={landingProspectId}
            selectedCmId={selectedCmId}
            onSelectCm={selectCm}
            onModeChange={(mode) => {
              setPlacementMode(mode);
              setPickedClientPin(null);
              if (mode === 'client' && canShowClients) setShowClients(true);
            }}
            onPinChange={onPlacementPinChange}
            onAssignProspect={onAssignProspect ?? (async () => {})}
            onAssignClient={onAssignClient ?? (async () => {})}
            onClose={clearPlacement}
          />
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2 text-sm text-ink-soft">
        {cmsWithoutHome.length > 0 && (
          <p data-testid="map-cms-without-home">
            Care managers with no home point on file: {cmsWithoutHome.map((c) => c.full_name).join(', ')}.
          </p>
        )}
        {canShowClients && showClients && clientsLoading && <p>Loading client pins.</p>}
        {canShowClients && showClients && clientsError && (
          <p role="alert" className="text-danger">The client pins could not be loaded.</p>
        )}
        {canShowClients && showClients && clientMap && (
          <div data-testid="map-not-placed">
            <p>
              {/* A client with no address is listed (its own group) but counted
                  only by the server's no-address count beside this one, so
                  the two numbers never overlap (LP-42). */}
              <span className="font-semibold text-ink">Not placed yet: {notPlacedWithAddress}</span>
              {clientMap.no_address_count !== null && clientMap.no_address_count > 0 && (
                <span> · No address on file: {clientMap.no_address_count}</span>
              )}
            </p>
            {notPlacedGroups.map((g) => (
              <NotPlacedGroup
                key={g.reason}
                label={clientNotPlacedLabel(g.reason, workingStates)}
                items={g.items}
                testId={`map-not-placed-${g.reason.replace(/_/g, '-')}`}
                itemTestId="map-not-placed-client"
              />
            ))}
            {notPlacedUngrouped.length > 0 && (
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {notPlacedUngrouped.map((c) => (
                  <li key={c.client_id}>
                    <Link
                      to={`/t/${tenantSlug}/clients/${c.client_id}`}
                      className="text-xs text-sage-700 bg-cream rounded-pill px-2 py-0.5 no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                      data-testid="map-not-placed-client"
                    >
                      {c.display_name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
